import {
  ActivitySquare,
  Bike,
  Dumbbell,
  Flower2,
  Footprints,
  HeartPulse,
  Layers3,
  Moon,
  PersonStanding,
  Wind,
} from 'lucide-react'
import { motion } from 'motion/react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { Discipline } from '@/hooks/useDashboardChannel'

export const DISCIPLINE_TABS: { value: Discipline; label: string; icon: typeof Bike }[] = [
  { value: 'all', label: 'All', icon: ActivitySquare },
  { value: 'cycling', label: 'Biking', icon: Bike },
  { value: 'running', label: 'Running', icon: PersonStanding },
  { value: 'walking', label: 'Walking', icon: Footprints },
  { value: 'pilates', label: 'Pilates', icon: Layers3 },
  { value: 'yoga', label: 'Yoga', icon: Flower2 },
  { value: 'stretching', label: 'Stretching', icon: Wind },
  { value: 'meditation', label: 'Meditation', icon: Moon },
  { value: 'cardio', label: 'Cardio', icon: HeartPulse },
  { value: 'strength', label: 'Strength', icon: Dumbbell },
]

interface DisciplineDockProps {
  value: Discipline
  onChange: (value: Discipline) => void
}

// Sticky pill dock: stays under the nav while you scroll, and the active
// highlight glides between options (shared layoutId) rather than snapping.
export function DisciplineDock({ value, onChange }: DisciplineDockProps) {
  return (
    <div className="sticky top-[3.6rem] z-30 flex justify-center px-4 py-3">
      <div className="w-full sm:hidden">
        <Select value={value} onValueChange={(v) => onChange(v as Discipline)}>
          <SelectTrigger className="w-full bg-background/70 backdrop-blur-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DISCIPLINE_TABS.map(({ value: v, label, icon: Icon }) => (
              <SelectItem key={v} value={v}>
                <Icon className="size-4" />
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div
        role="tablist"
        className="hidden max-w-full gap-1 overflow-x-auto rounded-full border border-border bg-background/60 p-1 shadow-lg shadow-black/10 backdrop-blur-xl sm:flex"
      >
        {DISCIPLINE_TABS.map(({ value: v, label, icon: Icon }) => {
          const active = v === value
          return (
            <button
              key={v}
              role="tab"
              aria-selected={active}
              aria-label={label}
              title={label}
              onClick={() => onChange(v)}
              className={`relative flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                active ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {active && (
                <motion.span
                  layoutId="dock-pill"
                  className="absolute inset-0 rounded-full bg-primary"
                  transition={{ type: 'spring', bounce: 0.22, duration: 0.55 }}
                />
              )}
              <Icon className="relative z-10 size-4" />
              <span className="relative z-10 hidden lg:inline">{label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
